import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useChatStore } from "@circle-react/hooks/chatsV2/store/useChatStore";
import { subscriptionEventsEmitter } from "@circle-react/hooks/subscriptionEventsEmitter";
import type { SubscriptionStateType } from "@circle-react/hooks/subscriptionEventsEmitter";
import type { Chat } from "@circle-react/types/CommunityInbox/Chat";
import type { Message } from "@circle-react/types/CommunityInbox/Message";
import { useEffect } from "react";

// The inbox channel carries the saved reply. Reconcile after it reconnects,
// without replacing the paginated history or changing the scroll action.
export const useAvatarReconnect = (
  chat: Chat | null | undefined,
  isDms: boolean,
) => {
  const chatId = chat?.id;
  const isStreamingEnabled = chat?.streaming_enabled;

  useEffect(() => {
    if (!isStreamingEnabled || !chatId) return;
    const channelName = isDms
      ? "CommunityInbox::MemberChannel"
      : "CommunityInbox::AdminChannel";
    const type = isDms ? "member" : "admin";
    let isDisconnected = false;
    let isDisposed = false;

    const reconcile = async () => {
      const before = useChatStore.getState().inbox;
      const [savedChat, { records }] = await Promise.all([
        reactQueryGet<Chat>(internalApi.communityInbox.show({ id: chatId })),
        reactQueryGet<{ records: Message[] }>(
          internalApi.communityInbox.fetchMessages({
            id: chatId,
            params: { page: 1 },
          }),
        ),
      ]);
      const inbox = useChatStore.getState().inbox;
      if (isDisposed || Number(inbox.currentChatId) !== chatId) return;

      // Socket updates received during the fetch are newer than this response.
      const chats = isDms ? inbox.memberChats : inbox.adminChats;
      const previousChats = isDms ? before.memberChats : before.adminChats;
      if (
        chats.find(({ id }) => id === chatId) ===
        previousChats.find(({ id }) => id === chatId)
      ) {
        inbox.updateChat(savedChat, type);
      }
      records.forEach(message => {
        const current = inbox.messages.find(({ id }) => id === message.id);
        const previous = before.messages.find(({ id }) => id === message.id);
        if (!current && !previous) inbox.appendMessage(message, type);
        else if (
          current &&
          ((!message.is_processing && current.is_processing) ||
            current === previous)
        ) {
          inbox.updateMessage(message);
        }
      });
      // Cancellation can delete the placeholder instead of saving a reply.
      if (!savedChat.is_processing) {
        before.messages
          .filter(message => message.is_processing)
          .forEach(message => {
            if (
              !records.some(({ id }) => id === message.id) &&
              inbox.messages.includes(message)
            ) {
              inbox.destroyMessage(message.id);
            }
          });
      }
    };

    const onStateChange = ({
      channel,
      subscriptionState,
    }: {
      channel: string;
      subscriptionState: SubscriptionStateType;
    }) => {
      if (channel !== channelName) return;
      if (subscriptionState === "disconnected") isDisconnected = true;
      else if (isDisconnected) {
        isDisconnected = false;
        void reconcile().catch(error =>
          console.error("Error refreshing Avatar reply", error),
        );
      }
    };
    subscriptionEventsEmitter.on("subscriptionStateChange", onStateChange);
    return () => {
      isDisposed = true;
      subscriptionEventsEmitter.off("subscriptionStateChange", onStateChange);
    };
  }, [chatId, isStreamingEnabled, isDms]);
};
